import { priceFrame } from "../lib/priceFrame";
import type { Pool } from "../types";

interface ExactValue {
  digits: bigint;
  scale: number;
}

function parse(value: string): ExactValue | null {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return null;
  const whole = match.at(1);
  if (!whole) return null;
  const fraction = match.at(2) ?? "";
  return { digits: BigInt(whole + fraction), scale: fraction.length };
}

function aligned(values: ExactValue[]): bigint[] {
  const scale = Math.max(...values.map((value) => value.scale));
  return values.map((value) => value.digits * 10n ** BigInt(scale - value.scale));
}

export function PriceTrend({ pool }: { pool: Pool }) {
  const history = pool.priceHistory7d;
  const frame = priceFrame(pool.tokenA, pool.tokenB);
  // Every sample goes through the rule frame first: identity in canonical, exact reciprocal when
  // inverted, and a non-positive or unparsable sample (including "0") becomes a gap, never a point.
  const parsed =
    history?.map((value) => {
      const displayed = frame.priceOrNull(value);
      return displayed === null ? null : parse(displayed);
    }) ?? [];
  const available = parsed.filter((value): value is ExactValue => value !== null);
  if (!history || available.length < 2)
    return (
      <span
        className="trend-unavailable"
        aria-label="Price trend unavailable"
        title="No price trend"
      >
        —
      </span>
    );
  const exact = aligned(available);
  const minimum = exact.reduce((left, right) => (left < right ? left : right));
  const maximum = exact.reduce((left, right) => (left > right ? left : right));
  const range = maximum - minimum;
  const byIndex = new Map<number, bigint>();
  let availableIndex = 0;
  parsed.forEach((value, index) => {
    if (value) byIndex.set(index, exact[availableIndex++] ?? 0n);
  });
  const point = (index: number, value: bigint) => {
    const x = history.length === 1 ? 50 : (index / (history.length - 1)) * 100;
    const ratio = range === 0n ? 500_000n : ((value - minimum) * 1_000_000n) / range;
    return `${x.toFixed(2)},${(27 - (Number(ratio) / 1_000_000) * 22).toFixed(2)}`;
  };
  const segments: string[][] = [];
  let segment: string[] = [];
  history.forEach((_value, index) => {
    const value = byIndex.get(index);
    if (value === undefined) {
      if (segment.length) segments.push(segment);
      segment = [];
    } else segment.push(point(index, value));
  });
  if (segment.length) segments.push(segment);
  if (!segments.some((points) => points.length > 1))
    return (
      <span
        className="trend-unavailable"
        aria-label="Price trend unavailable"
        title="No price trend"
      >
        —
      </span>
    );
  const first = exact[0] ?? 0n;
  const last = exact.at(-1) ?? first;
  const direction = last > first ? "up" : last < first ? "down" : "flat";
  const label = `7 day price trend for ${frame.pair}, ${direction}`;
  return (
    <svg className={`price-trend ${direction}`} viewBox="0 0 100 32" role="img" aria-label={label}>
      {segments.map((points, index) =>
        points.length > 1 ? <polyline key={index} points={points.join(" ")} /> : null,
      )}
    </svg>
  );
}
